// ActivityFeedList — recursive renderer for ActivityFeedItem[].

import type { SessionEvent } from '@localagent/sdk';
import { type ActivityFeedItem } from './activity-feed';
import { AssistantTurnItem } from './AssistantTurnItem';
import { ToolInvocationItem } from './ToolInvocationItem';
import { SubAgentActivityItem } from './SubAgentActivityItem';

export interface ActivityFeedListProps {
  items: ActivityFeedItem[];
  events: readonly SessionEvent[];
  getEventData: (seq: number) => Promise<Record<string, unknown>>;
  /** When true, every item keeps nested details open. */
  showAllDetails?: boolean;
  /**
   * When true (and `showAllDetails` is false), only the last item in this list
   * keeps nested details open — previous steps collapse as the next starts.
   */
  focusLast?: boolean;
}

export function ActivityFeedList({
  items,
  events,
  getEventData,
  showAllDetails = false,
  focusLast = false,
}: ActivityFeedListProps) {
  const activeKey =
    !showAllDetails && focusLast && items.length > 0 ? items[items.length - 1]!.key : null;

  return (
    <>
      {items.map((item) => (
        <ActivityFeedRow
          key={item.key}
          item={item}
          events={events}
          getEventData={getEventData}
          showAllDetails={showAllDetails}
          detailsOpen={showAllDetails || item.key === activeKey}
        />
      ))}
    </>
  );
}

function ActivityFeedRow({
  item,
  events,
  getEventData,
  showAllDetails,
  detailsOpen,
}: {
  item: ActivityFeedItem;
  events: readonly SessionEvent[];
  getEventData: (seq: number) => Promise<Record<string, unknown>>;
  showAllDetails: boolean;
  detailsOpen: boolean;
}) {
  if (item.kind === 'assistant_turn') {
    return <AssistantTurnItem step={item.step} detailsOpen={detailsOpen} />;
  }

  if (item.kind === 'tool_invocation') {
    return (
      <ToolInvocationItem
        step={item.step}
        child={item.child}
        detailsOpen={detailsOpen}
        childContent={
          item.child ? (
            <ActivityFeedList
              items={item.child.items}
              events={events}
              getEventData={getEventData}
              showAllDetails={showAllDetails}
              focusLast={detailsOpen && !showAllDetails}
            />
          ) : undefined
        }
      />
    );
  }

  // Unmatched sibling sub-agent
  return (
    <SubAgentActivityItem feed={item.feed}>
      {detailsOpen ? (
        <ActivityFeedList
          items={item.feed.items}
          events={events}
          getEventData={getEventData}
          showAllDetails={showAllDetails}
          focusLast={detailsOpen && !showAllDetails}
        />
      ) : null}
    </SubAgentActivityItem>
  );
}
